import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks';
import { useNavigate, Link } from 'react-router-dom';
import { getToken, buildApiUrl } from '@/utils';
import { Modal } from './ui/Modal';

type WhatsAppStatus = 'connecting' | 'connected' | 'disconnected' | 'qr_ready' | 'unknown';

/**
 * Header component with navigation and logout
 */
export const Header: React.FC = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [whatsappStatus, setWhatsappStatus] = useState<WhatsAppStatus>('unknown');
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Connect to SSE stream for WhatsApp connection status
  useEffect(() => {
    if (!admin?.shop_id) return;
    
    let eventSource: EventSource | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let mounted = true;

    const connectSSE = () => {
      if (!mounted) return;
      
      const token = getToken();
      if (!token) return;

      setIsCheckingStatus(true);
      const url = buildApiUrl(`shops/${admin.shop_id}/status/stream?token=${encodeURIComponent(token)}`);
      
      eventSource = new EventSource(url);

      eventSource.addEventListener('message', (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'status') {
            const statusValue = data.status || 'disconnected';
            setWhatsappStatus(statusValue as WhatsAppStatus);
            setIsCheckingStatus(false);
          } else if (data.type === 'connected' || data.type === 'disconnected') {
            // Also handle direct connection updates from qrService.addListener
            setWhatsappStatus(data.type as WhatsAppStatus);
            setIsCheckingStatus(false);
          } else if (data.type === 'error') {
            setWhatsappStatus('disconnected');
          }
        } catch (error) {
          console.error('[Header] Error parsing SSE message:', error);
        }
      });

      eventSource.addEventListener('error', () => {
        if (mounted) {
          console.warn('[Header] SSE connection error, reconnecting in 5s...');
          setWhatsappStatus('disconnected');
          if (eventSource) {
            eventSource.close();
            eventSource = null;
          }
          // Attempt reconnection after 5 seconds
          reconnectTimeout = setTimeout(connectSSE, 5000);
        }
      });
    };

    connectSSE();

    return () => {
      mounted = false;
      if (eventSource) {
        eventSource.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
    };
  }, [admin?.shop_id]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogoutClick = () => {
    setIsLogoutConfirmOpen(true);
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      setIsLogoutConfirmOpen(false);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="bg-slate-950 text-white shadow">
      <div className="max-w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Nav Links */}
          <div className="flex items-center gap-8">
            <Link to="/dashboard" className="flex items-center gap-2">
              <img src="/logo-icon.png" alt="PrintDeck Logo" className="w-8 h-8 object-contain" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-emerald-400 bg-clip-text text-transparent">
                PrintDeck Direct
              </span>
            </Link>

            {/* Navigation Menu */}
            {admin && (
              <nav className="hidden md:flex items-center gap-6 text-sm font-medium">
                <Link
                  to="/dashboard"
                  className="text-slate-300 hover:text-white transition-colors"
                >
                  Dashboard
                </Link>
                <Link
                  to="/settings"
                  className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
                >
                  ⚙️ Settings
                </Link>

                {/* WhatsApp Status Indicator */}
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
                  {whatsappStatus === 'connected' ? (
                    <>
                      <span className="inline-block h-2 w-2 bg-green-500 rounded-full"></span>
                      <span className="text-xs font-medium text-green-400">WhatsApp Connected</span>
                    </>
                  ) : whatsappStatus === 'connecting' || whatsappStatus === 'qr_ready' ? (
                    <>
                      <span className="inline-block h-2 w-2 bg-yellow-500 rounded-full animate-pulse"></span>
                      <span className="text-xs font-medium text-yellow-400">Connecting...</span>
                    </>
                  ) : (
                    <>
                      <span className="inline-block h-2 w-2 bg-red-500 rounded-full"></span>
                      <span className="text-xs font-medium text-red-400">Disconnected</span>
                    </>
                  )}
                </div>
              </nav>
            )}
          </div>

          {/* User Info & Logout */}
          {admin && (
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right text-sm">
                <div className="flex items-center gap-2 justify-end">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-300 border border-blue-500/30">
                    Trial Plan
                  </span>
                  <p className="font-medium text-slate-200">{admin.email}</p>
                </div>
                <p className="text-slate-400 text-xs capitalize">{admin.role}</p>
              </div>
              <button
                onClick={handleLogoutClick}
                className="px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-800 hover:bg-slate-700 text-sm font-medium transition-colors"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <Modal
        isOpen={isLogoutConfirmOpen}
        title="Confirm Logout"
        onClose={() => !isLoggingOut && setIsLogoutConfirmOpen(false)}
        size="sm"
        actions={[
          {
            label: 'Logout',
            onClick: handleConfirmLogout,
            variant: 'danger',
            isLoading: isLoggingOut,
          },
        ]}
      >
        <p className="text-slate-600">
          Are you sure you want to logout? You'll need to sign in again to access your account.
        </p>
      </Modal>
    </header>
  );
};

export default Header;
